import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Image,
  Video,
  Paperclip,
  Link2,
  Code,
  CheckSquare,
  CalendarDays,
  Layers,
  Folder,
  Send,
  FileText,
  Kanban,
  Lock,
  Reply,
  Forward,
  User,
  Hash,
  Calendar,
} from 'lucide-react';
import usePopoverPosition from '../../hooks/usePopoverPosition';
import UserAvatar from '../ui/UserAvatar';
import DatePicker from '../ui/DatePicker';
import './ChatSearch.css';

const HAS_ICONS = { Image, Video, Paperclip, Link2, Code, CheckSquare, CalendarDays, Layers, Folder, Send, FileText, Kanban, Lock, Reply, Forward };
const KEY_ICONS = { from: User, has: Hash, before: Calendar, after: Calendar, during: Calendar };
const TITLE_KEYS = {
  keys: 'chat.search.optionsTitle',
  has: 'chat.search.containsTitle',
  from: 'chat.search.fromTitle',
  date: 'chat.search.dateTitle',
};
// Values whose row carries an extra hint line (what the filter cannot find).
const HAS_HINTS = { forward: 'chat.search.has.forwardHint', link: 'chat.search.has.linkHint' };

/**
 * The Discord-style suggestion menu under the chat search field: search
 * options (keys), "Message contains" (has:), members (from:) and dates.
 * Presentational — the rows come from utils/chatSearchSuggestions and the
 * search bar owns the keyboard. Rows keep the input focused (mousedown is
 * prevented) so picking one never blurs the field.
 */
export default function ChatSearchAutocomplete({ anchorRef, menuRef, listId, ctx, items, activeIndex, onPick, onHover, onPickDate }) {
  const { t } = useTranslation();
  const pos = usePopoverPosition({ anchorRef, open: true, width: 300, placement: 'bottom', align: 'end' });
  if (!ctx || !items.length) return null;

  const renderRow = (item) => {
    if (item.kind === 'pick') {
      return (
        <div className="chat-search-ac-pick">
          <DatePicker
            value={null}
            onChange={(v) => v && onPickDate(v)}
            placeholder={t('chat.search.date.pick')}
            aria-label={t('chat.search.date.pick')}
          />
        </div>
      );
    }
    let icon = null;
    let main = null;
    let sub = null;
    if (item.kind === 'key') {
      const Icon = KEY_ICONS[item.key];
      icon = <Icon size={14} />;
      main = <span className="chat-search-ac-token">{item.token}</span>;
      sub = t(`chat.search.key.${item.key}`);
    } else if (item.kind === 'has') {
      const Icon = HAS_ICONS[item.icon] || Hash;
      icon = <Icon size={14} />;
      main = t(item.labelKey);
      sub = HAS_HINTS[item.value] ? t(HAS_HINTS[item.value]) : null;
    } else if (item.kind === 'member') {
      icon = <UserAvatar className="chat-search-ac-avatar" userId={item.member.id} name={item.member.name} size={20} />;
      main = item.member.name;
    } else {
      icon = <Calendar size={14} />;
      main = t(item.labelKey);
      sub = item.token.split(':')[1];
    }
    return (
      <>
        <span className="chat-search-ac-icon">{icon}</span>
        <span className="chat-search-ac-text">
          <span className="chat-search-ac-main">{main}</span>
          {sub && <span className="chat-search-ac-sub">{sub}</span>}
        </span>
        {item.kind === 'has' && <span className="chat-search-ac-value">{item.value}</span>}
      </>
    );
  };

  return createPortal(
    <div
      ref={menuRef}
      id={listId}
      className="chat-search-ac panel-scroll"
      role="listbox"
      aria-label={t(TITLE_KEYS[ctx.mode])}
      style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight }}
      onMouseDown={(e) => {
        // keep focus in the input; the date picker's own trigger still works
        if (!e.target.closest('.chat-search-ac-pick')) e.preventDefault();
      }}
    >
      <div className="chat-search-ac-title">{t(TITLE_KEYS[ctx.mode])}</div>
      {items.map((item, i) =>
        item.kind === 'pick' ? (
          <div key={item.id}>{renderRow(item)}</div>
        ) : (
          <div
            key={item.id}
            role="option"
            aria-selected={i === activeIndex}
            data-token={item.token}
            className={`chat-search-ac-row${i === activeIndex ? ' chat-search-ac-row--active' : ''}`}
            onMouseEnter={() => onHover(i)}
            onClick={() => onPick(item)}
          >
            {renderRow(item)}
          </div>
        )
      )}
    </div>,
    document.body
  );
}
